import {
  DefaultSupplyPrice,
  DefaultSupplyPriceType,
  Partner,
  PriceType,
  PriceTypeType,
  SourcingStrategy,
  SourcingStrategyType,
} from '@/api/hooks/useTenantConfig'
import { useTenantStore } from '@/stores/tenantStore'
import { Texto } from '@gravitate-js/excalibrr'
import { Form, Modal, Select, Switch, Typography } from 'antd'
import _ from 'lodash'
import { useEffect } from 'react'

const { Text } = Typography

const strategyDescriptions: Record<SourcingStrategyType, string> = {
  [SourcingStrategy.SPECIFIC_SUPPLY]:
    'Will only create an order if the requested supply resolves to a single supply option. This is ideal for orders requiring a specific type of supply without alternatives.',
  [SourcingStrategy.TANK_SUPPLY_DEFAULT]:
    'Will only create an order if the requested drops have a default supply option configured in the system. This strategy ensures orders are fulfilled using pre-defined supply preferences.',
  [SourcingStrategy.USE_BEST]:
    'Will resolve supply options using the supplied parameters and then choose the best supply option from the remaining choices. This strategy optimizes for cost and availability when a specific supply is not required.',
  [SourcingStrategy.MANUAL]:
    'Will submit a request to have an order manually created. This option is used when automated processes cannot meet the specific needs of the order or when human intervention is preferred for decision-making.',
}

export type SourcingConfig = {
  allow_alternate_products: boolean
  allow_any_blends: boolean
  strategy: SourcingStrategyType
  manual_supply_fallback: boolean
  fit_to_trailer: boolean
  allow_load_fanout: boolean
  default_price_type: PriceTypeType | null
  use_contract_numbers: boolean
  use_load_number: boolean
  default_supply_price: DefaultSupplyPriceType
  use_placeholder_prices: boolean
  create_missing_prices: boolean
}

export type SourcingConfigModalProps = {
  open: boolean
  onClose: () => void
  partner: Partner | null
  onSave: (config: SourcingConfig) => void
  loading?: boolean
}

export function SourcingConfigModal({
  partner,
  open,
  onClose,
  onSave,
  loading,
}: SourcingConfigModalProps) {
  const [form] = Form.useForm<SourcingConfig>()
  const { selectedTenant } = useTenantStore()
  const usePlaceholderPrices = Form.useWatch('use_placeholder_prices', form)

  useEffect(() => {
    if (partner && open) {
      const sourcingConfig = partner.sourcing_config
      if (!sourcingConfig) return
      form.setFieldsValue({
        allow_alternate_products:
          sourcingConfig.allow_alternate_products ?? false,
        allow_any_blends: sourcingConfig.allow_any_blends ?? false,
        strategy: sourcingConfig.strategy ?? SourcingStrategy.USE_BEST,
        manual_supply_fallback: sourcingConfig.manual_supply_fallback ?? true,
        fit_to_trailer: sourcingConfig.fit_to_trailer ?? false,
        allow_load_fanout: sourcingConfig.allow_load_fanout ?? false,
        default_price_type: sourcingConfig.default_price_type ?? null,
        use_contract_numbers: sourcingConfig.use_contract_numbers ?? false,
        use_load_number: sourcingConfig.use_load_number ?? false,
        default_supply_price:
          sourcingConfig.default_supply_price ?? DefaultSupplyPrice.MIN,
        use_placeholder_prices: sourcingConfig.use_placeholder_prices ?? false,
        create_missing_prices: sourcingConfig.create_missing_prices ?? false,
      })
    }
  }, [partner, open, form])

  const handleOk = () => {
    form.validateFields().then((values) => {
      onSave(values)
    })
  }

  return (
    <Modal
      title='Sourcing Configuration'
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText='Save Configuration'
      confirmLoading={loading}
      width={500}
    >
      <Form form={form}>
        <Texto appearance='hint'>
          These options control how orders are sourced in {selectedTenant?.name}{' '}
          when received from {partner?.tenant_name}{' '}
          {partner?.partner_instance_name}.
        </Texto>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            marginTop: 20,
            marginBottom: 40,
          }}
        >
          <div>
            <div>
              <p className='mb-2'>Sourcing Strategy</p>
              <Form.Item name='strategy' style={{ marginBottom: 0 }}>
                <Select
                  placement='bottomRight'
                  style={{ width: '100%' }}
                  popupMatchSelectWidth={false}
                  options={Object.entries(SourcingStrategy).map(
                    ([_key, value]) => ({
                      label: value,
                      value: value,
                    }),
                  )}
                  optionRender={(option) => (
                    <div style={{ padding: '4px 0', maxWidth: 350 }}>
                      <div style={{ fontWeight: 500 }}>{option.label}</div>
                      <Text
                        type='secondary'
                        style={{ fontSize: 12, whiteSpace: 'normal' }}
                      >
                        {
                          strategyDescriptions[
                            option.value as SourcingStrategyType
                          ]
                        }
                      </Text>
                    </div>
                  )}
                />
              </Form.Item>
            </div>
          </div>
          <div>
            <p className='mb-2'>Default Price Type</p>
            <Form.Item name='default_price_type' style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                options={[
                  { label: 'No Default', value: null },
                  ...Object.values(PriceType).map((value) => ({
                    label: _.startCase(value),
                    value,
                  })),
                ]}
              />
            </Form.Item>
            <Text type='secondary' style={{ fontSize: 12 }}>
              The price type used for sourced supply when no price type is
              otherwise specified.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='allow_alternate_products'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' />
              </Form.Item>
              <span>Allow Alternate Products</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              Determines whether alternate products can be used as substitutes
              for the requested products.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='allow_any_blends'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' />
              </Form.Item>
              <span>Allow Any Blends</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              Removes the product constraint from loads, allowing the S&D system
              to fulfill using any blend components.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='manual_supply_fallback'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' />
              </Form.Item>
              <span>Manual Supply Fallback</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              Indicates if a manual request should be submitted when the order
              cannot be automatically sourced.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='fit_to_trailer'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' />
              </Form.Item>
              <span>Fit to Trailer</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              Will modify the volume of the order to fit the trailer in the
              destination system. When disabled, creates a by-product order.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='allow_load_fanout'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' />
              </Form.Item>
              <span>Allow Load Fanout</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              When receiving orders from this partner, load mappings are allowed
              to fanout and be translated from master data into multiple
              options.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='use_placeholder_prices'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch
                  size='small'
                  onChange={(checked) => {
                    if (!checked) {
                      form.setFieldValue('create_missing_prices', false)
                    }
                  }}
                />
              </Form.Item>
              <span>Use Placeholder Prices</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              When receiving orders from this partner, allow extending expired
              price curves to bypass sourcing errors.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='create_missing_prices'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' disabled={!usePlaceholderPrices} />
              </Form.Item>
              <span>Create Missing Prices</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              When receiving orders from this partner, allow creating new price
              curves to bypass sourcing errors. This option requires Use
              Placeholder Prices to be enabled.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='use_contract_numbers'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' />
              </Form.Item>
              <span>Use Contract Numbers</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              When enabled, contract numbers from received orders are forwarded
              to the partner's S&D system. When disabled, contract numbers are
              stripped from outbound order create and update requests.
            </Text>
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Form.Item
                name='use_load_number'
                valuePropName='checked'
                style={{ marginBottom: 0 }}
              >
                <Switch size='small' />
              </Form.Item>
              <span>Use Load Numbers</span>
            </div>
            <Text type='secondary' style={{ fontSize: 12, marginLeft: 36 }}>
              When enabled, load numbers from received orders are forwarded to
              the partner's S&D system. When disabled, load numbers are stripped
              from outbound order create and update requests.
            </Text>
          </div>
          <div>
            <p className='mb-2'>Default Supply Price</p>
            <Form.Item name='default_supply_price' style={{ marginBottom: 0 }}>
              <Select
                style={{ width: '100%' }}
                options={[
                  {
                    label: `${DefaultSupplyPrice.MIN}`,
                    value: DefaultSupplyPrice.MIN,
                  },
                  {
                    label: `${DefaultSupplyPrice.MAX}`,
                    value: DefaultSupplyPrice.MAX,
                  },
                ]}
              />
            </Form.Item>
            <Text type='secondary' style={{ fontSize: 12 }}>
              Prices created during the supply plan integration will use this
              price.
            </Text>
          </div>
        </div>
      </Form>
    </Modal>
  )
}
